import { useTranslation } from "react-i18next";
import {
  IconAdjustments,
  IconHeartHandshake,
  IconMicroscope,
  IconPaperclip,
  IconShieldLock,
} from "@tabler/icons-react";
import BottomPanel from "../app/panels/BottomPanel";
import { DesktopDialog } from "../app/DesktopDialog";
import { cn } from "../../lib/utils";
import { useApiQuery } from "../../services/api";
import { RoomSupportedByChip } from "./RoomSupportedByChip";
import { RoomContactLine } from "./RoomContactLine";
import { useCurrentUser } from "../../domains/user/hooks/useCurrentUser";
import { contactLineKind } from "../../domains/partners/roomWelcome";

/**
 * What a room is, from the info button of its "Supported by" badge: a bottom
 * sheet on a phone, a `DesktopDialog` on a desktop (Figma Desktop 5601:47051,
 * opened by the ⓘ, with no button).
 */
export default function RoomExplainerBottomSheet({
  roomId,
  presentation,
  isOpen,
  onClose,
}: {
  roomId: string;
  presentation: "sheet" | "dialog";
  isOpen: boolean;
  onClose: () => void;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const roomQuery = useApiQuery("/webapp/private/rooms/:id", {
    param: { id: roomId },
  });
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  const { currentUser } = useCurrentUser();
  if (currentRoom === null) return null;

  // The same contact line as the welcome sheet, conditional before a join
  // (docs/partner-objection.md, REQ-07, REQ-08).
  const contactLine = contactLineKind(
    currentRoom.partnerContact,
    currentUser !== null && currentUser.rooms.includes(roomId)
  );

  const isDialog = presentation === "dialog";
  const title = t("A focused scientific room on one topic");

  // The four rows spell out the trust gradation from the Rooms positioning
  // doc — independent (Juisci curates) → supported (partner funds) →
  // contributed (partner's Resources) → HCP-controlled (contact sharing). The
  // gradation is never named on screen: we describe the mechanisms and let the
  // reader conclude. Factual on purpose — no reassuring adjectives.
  const rows = [
    {
      Icon: IconMicroscope,
      title: t("We select the science"),
      body: t(
        "Our independent medical team oversees the AI that selects every study. Ensuring quality, relevance, and diverse perspectives."
      ),
    },
    {
      Icon: IconHeartHandshake,
      title: t("Partner supports the room"),
      body: t(
        "Partners fund the space but don't steer the content. Publications in Discovery remain neutral and unbiased."
      ),
    },
    {
      Icon: IconPaperclip,
      title: t("Partner shares resources"),
      body: t(
        "In the Resources section, the partner shares approved, non-promotional medical content."
      ),
    },
    {
      // Figma D3 (5601:47051) draws the settings of the reader here.
      Icon: isDialog ? IconAdjustments : IconShieldLock,
      title: t("You stay in control"),
      body: t(
        "Set your filters and explore freely. Sharing your contact is optional and reversible at any time."
      ),
    },
  ];

  const chip = (
    <RoomSupportedByChip
      organisationName={currentRoom.organisationModel.name}
      size="md"
    />
  );

  const rowList = (
    <div
      className={cn(
        "flex w-full flex-col",
        isDialog ? "gap-3.5" : "gap-6 py-2"
      )}
    >
      {rows.map((row) => (
        <div
          key={row.title}
          className={cn("flex items-start", isDialog ? "gap-3" : "gap-3.5")}
        >
          <span
            className={cn(
              "flex shrink-0 items-center justify-center rounded-lg",
              isDialog ? "bg-ds-slate-100 p-1.5" : "size-9.5 bg-ds-black/6"
            )}
          >
            <row.Icon
              className={cn(
                isDialog
                  ? "size-4 text-ds-slate-700"
                  : "size-5 text-ds-moss-600"
              )}
              stroke={1.5}
            />
          </span>
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <h3
              className={cn(
                "m-0 font-dm-sans",
                isDialog
                  ? "text-sm font-medium text-ds-black"
                  : "text-base font-bold leading-[1.4] text-ds-mint-800"
              )}
            >
              {row.title}
            </h3>
            <p
              className={cn(
                "m-0 font-dm-sans font-normal text-ds-slate-600",
                isDialog ? "text-desktop-meta" : "text-footnote"
              )}
            >
              {row.body}
            </p>
          </div>
        </div>
      ))}
    </div>
  );

  const contactRow =
    contactLine === "none" ? null : (
      <RoomContactLine
        kind={contactLine}
        partnerName={currentRoom.organisationModel.name}
        presentation={presentation}
      />
    );

  if (isDialog) {
    if (!isOpen) return null;
    // Figma Desktop 5601:47051: the close cross alone on its row, then the
    // title and the chip together.
    return (
      <DesktopDialog
        presentation="modal"
        title={title}
        header={{ layout: "stacked", leading: null }}
        subtitle={chip}
        onClose={onClose}
        footer={contactRow}
      >
        {rowList}
      </DesktopDialog>
    );
  }

  return (
    <BottomPanel
      isOpen={isOpen}
      onClose={onClose}
      backdropBlur
      displayCloseButton
    >
      <div className="flex flex-col items-start gap-3 px-6 pb-6 pt-12">
        <h2 className="m-0 font-dm-sans text-heading-lg font-semibold text-ds-mint-900">
          {title}
        </h2>
        {chip}
        {rowList}
        {contactRow}
      </div>
    </BottomPanel>
  );
}
